import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { IconChevronRight as ChevronRight } from '@tabler/icons-react';

export const wrap = 'mx-auto w-full max-w-[1200px] px-4 xl:px-0';

export const pageBg = 'bg-white dark:bg-nightBg transition-colors';

export const displayTitle = 'text-[clamp(40px,6.5vw,88px)] font-bold leading-[0.95] tracking-[-0.05em] text-grey10 dark:text-white';
export const sectionTitle = 'text-[clamp(28px,4vw,48px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white';
export const cardTitle = 'text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white';
export const eyebrow = 'text-sm font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted';
export const bodyMuted = 'text-base leading-relaxed text-grey40 dark:text-nightMuted';

export const surface = 'rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]';
export const card = 'rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10';
export const darkPanel = 'relative isolate overflow-hidden rounded-[36px] bg-[#0a0a0a] text-white dark:bg-[#101010] dark:ring-1 dark:ring-white/10';

export const btnPrimary = 'inline-flex h-12 items-center justify-center gap-2 rounded-full bg-grey10 px-6 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60 dark:bg-white dark:text-nightBg dark:hover:bg-grey90';
export const btnAccent = 'inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-white transition-colors hover:bg-primary30 disabled:opacity-60 dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70';
export const btnGhost = 'inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 disabled:opacity-60 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]';

export const chip = 'h-10 shrink-0 rounded-full px-4 text-sm font-semibold transition-colors';
export const chipActive = 'bg-primary text-white dark:bg-nightAccent dark:text-nightBg';
export const chipIdle = 'bg-grey99 text-grey20 hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10';

export const input = 'h-12 w-full rounded-2xl bg-grey99 px-4 text-base text-grey10 ring-1 ring-black/[0.04] transition-shadow placeholder:text-grey60 focus:outline-none focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60';
export const label = 'mb-2 block text-sm font-semibold text-grey20 dark:text-nightTextSecondary';

export const glow = 'pointer-events-none absolute -top-48 left-1/2 -z-10 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-nightAccent/20 blur-[120px] max-md:hidden dark:bg-nightAccent/15';

export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
    return (
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
            {items.map((item, i) => (
                <span key={`${item.label}-${i}`} className="flex items-center gap-1.5">
                    {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-grey70 dark:text-white/30" />}
                    {item.to ? (
                        <Link to={item.to} className="text-grey40 transition-colors hover:text-grey10 dark:text-nightMuted dark:hover:text-white">{item.label}</Link>
                    ) : (
                        <span className="text-grey10 dark:text-white">{item.label}</span>
                    )}
                </span>
            ))}
        </nav>
    );
}

export function PageHero({ eyebrow: kicker, title, subtitle, children, className = '' }: {
    eyebrow?: ReactNode;
    title: ReactNode;
    subtitle?: ReactNode;
    children?: ReactNode;
    className?: string;
}) {
    return (
        <section className={`relative isolate ${className}`}>
            <div className={glow} />
            <div className={`${wrap} pt-10 sm:pt-16`}>
                {kicker && <p className={eyebrow}>{kicker}</p>}
                <h1 className={`${displayTitle} ${kicker ? 'mt-4' : ''}`}>{title}</h1>
                {subtitle && <p className={`mt-6 max-w-[560px] text-lg ${bodyMuted}`}>{subtitle}</p>}
                {children}
            </div>
        </section>
    );
}

export function EmptyState({ title, text, action }: { title: ReactNode; text?: ReactNode; action?: ReactNode }) {
    return (
        <div className="flex flex-col items-center rounded-[28px] bg-grey99 px-6 py-20 text-center dark:bg-white/[0.04]">
            <div className="text-[clamp(28px,4vw,44px)] font-bold tracking-[-0.04em] text-grey10 dark:text-white">{title}</div>
            {text && <p className="mt-3 max-w-sm text-base text-grey40 dark:text-nightMuted">{text}</p>}
            {action && <div className="mt-8">{action}</div>}
        </div>
    );
}

export function Segmented({ options, value, onChange, label: ariaLabel }: {
    options: { label: ReactNode; key: string }[];
    value: string;
    onChange: (key: string) => void;
    label: string;
}) {
    return (
        <div role="radiogroup" aria-label={ariaLabel} className="inline-flex shrink-0 rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]">
            {options.map(option => {
                const active = option.key === value;
                return (
                    <button
                        key={option.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => onChange(option.key)}
                        className={`h-9 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-all ${active
                            ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg'
                            : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'}`}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

export const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
export const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
export const inputClassNames = { inputWrapper: `h-14 ${fieldWrap}`, input: fieldText };
export const textareaClassNames = { inputWrapper: `py-3 ${fieldWrap}`, input: fieldText };
